import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { useCounselingStore } from '../store/counselingStore'
import CategorySelector from '../components/CategorySelector'
import ChatMessage from '../components/ChatMessage'
import ChatInput from '../components/ChatInput'
import SajuCard from '../components/SajuCard'
import Spinner from '../components/Spinner'
import { toast } from '../components/Toast'

const CATEGORY_LABELS: Record<string, string> = {
  love: '💕 연애/결혼', career: '💼 직업/커리어', relationship: '👥 인간관계',
  money: '💰 재물/재테크', health: '🌿 건강', general: '🔮 전체 운세',
}

const FAQ: Record<string, string[]> = {
  general: ['올해 전체적인 운세는?', '이번 달 조심해야 할 것은?', '지금 중요한 결정을 내려도 될까요?'],
  love: ['올해 연애운은?', '지금 만나는 사람과 잘 될까요?', '언제 좋은 사람을 만날 수 있을까요?'],
  career: ['이직 시기가 좋을까요?', '현재 직장에서 승진 가능성은?', '창업을 해도 될까요?'],
  money: ['올해 재물운은?', '투자를 시작해도 좋을까요?', '부수입을 늘릴 방법은?'],
  relationship: ['가족과의 갈등을 어떻게 풀까요?', '친구와의 관계가 불편합니다', '직장 동료와의 관계는?'],
  health: ['올해 건강운은?', '주의해야 할 건강 문제는?', '몸이 자주 피곤한 이유가 있을까요?'],
}

interface QnaPanelProps {
  session: any
  onSwitchToChat: () => void
}

function QnaPanel({ session, onSwitchToChat }: QnaPanelProps) {
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const faqs = FAQ[session.category] ?? FAQ.general

  const handleAsk = async (q: string) => {
    if (!q.trim() || loading) return
    setLoading(true)
    setAnswer(null)
    try {
      const { data } = await client.post(`/counseling/sessions/${session.id}/chat`, { content: q })
      setAnswer(data.content)
    } catch {
      toast('AI 응답 중 오류가 발생했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleFaq = (q: string) => {
    setQuestion(q)
    handleAsk(q)
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {!answer && !loading && (
          <>
            <div className="text-center text-[var(--color-text-tertiary)] mb-8">
              <p className="text-4xl mb-3">❓</p>
              <p className="text-sm">궁금한 것을 질문하면 단번에 답해드립니다.</p>
              <p className="text-xs text-[var(--color-text-disabled)] mt-1">대화 흐름 없이 빠르게 답변을 받을 수 있는 모드입니다.</p>
            </div>
            <div className="mb-4">
              <p className="text-[var(--color-text-tertiary)] text-xs mb-2">자주 묻는 질문</p>
              <div className="flex flex-wrap gap-2">
                {faqs.map((q) => (
                  <button key={q} onClick={() => handleFaq(q)}
                    className="px-3 py-1.5 bg-[var(--color-bg)] hover:bg-[var(--color-accent)]/10 hover:border-[var(--color-border-accent)] border border-[var(--color-border-default)] rounded-full text-[var(--color-text-secondary)] text-xs transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <Spinner size="lg" />
            <p className="text-[var(--color-text-tertiary)] text-sm">답변을 생성하고 있습니다...</p>
          </div>
        )}

        {answer && (
          <div className="space-y-4">
            {/* 질문 */}
            <div className="flex justify-end">
              <div className="max-w-[75%] bg-[var(--color-accent)] text-[var(--color-primary)] rounded-2xl rounded-br-sm px-4 py-3 text-sm break-words">
                {question}
              </div>
            </div>
            {/* 답변 */}
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-[var(--color-accent)]/20 border border-[var(--color-accent)]/30 flex items-center justify-center text-sm flex-shrink-0">🏪</div>
              <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-[var(--color-text-primary)] max-w-[85vw] sm:max-w-prose break-words">
                <div className="prose prose-sm max-w-none prose-p:text-[var(--color-text-primary)] prose-headings:text-[var(--color-text-primary)]">
                  <ReactMarkdown>{answer}</ReactMarkdown>
                </div>
              </div>
            </div>

            {/* 다시 묻기 / 대화로 이어가기 */}
            <div className="flex gap-3 pt-4">
              <button onClick={() => { setAnswer(null); setQuestion('') }}
                className="flex-1 py-2.5 rounded-xl border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] text-sm hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-accent)] transition-colors">
                🔄 다시 묻기
              </button>
              <button onClick={onSwitchToChat}
                className="flex-1 py-2.5 rounded-xl bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] text-sm font-bold transition-opacity">
                💬 대화로 이어가기
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 질문 입력 */}
      {!answer && (
        <div className="px-4 py-3 border-t border-[var(--color-border-default)]">
          <div className="flex gap-2">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(question) } }}
              placeholder="궁금한 것을 자유롭게 질문해 보세요..."
              rows={3}
              disabled={loading}
              className="flex-1 bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl px-3 py-2.5 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-disabled)] focus:outline-none focus:border-[var(--color-border-accent)] resize-none disabled:opacity-50"
            />
            <button onClick={() => handleAsk(question)} disabled={loading || !question.trim()}
              className="px-4 py-2 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-[var(--color-primary)] font-bold text-sm transition-opacity self-end">
              전송
            </button>
          </div>
          <p className="text-[var(--color-text-disabled)] text-xs mt-1.5">Enter로 전송 · Shift+Enter 줄바꿈</p>
        </div>
      )}
    </div>
  )
}

export default function Counseling() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAuthenticated, logout } = useAuthStore()
  const {
    sessions, currentSession, messages, streaming, streamingContent,
    setSessions, setCurrentSession, setMessages, addMessage,
    setStreaming, appendStreamingContent, clearStreamingContent,
  } = useCounselingStore()

  const [category, setCategory] = useState('general')
  const [mode, setMode] = useState<'chat' | 'qna'>('chat')
  const [showNewSession, setShowNewSession] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const autoStartedRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }

    const state = location.state as { autoStart?: boolean; category?: string; mode?: 'chat' | 'qna' } | null
    if (state?.autoStart && !autoStartedRef.current) {
      autoStartedRef.current = true
      const cat = (state.category || 'general') as string
      const mod = state.mode || 'chat'
      setCategory(cat)
      navigate(location.pathname, { replace: true, state: {} })
      ;(async () => {
        try {
          const { data } = await client.post('/counseling/sessions', { category: cat, mode: mod })
          setCurrentSession(data)
          setMessages([])
        } catch {
          toast('세션 생성에 실패했습니다.', 'error')
        }
        loadSessions()
      })()
    } else {
      loadSessions()
    }
  }, [isAuthenticated])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingContent])

  const loadSessions = async () => {
    setLoadingSessions(true)
    try {
      const { data } = await client.get('/counseling/sessions')
      setSessions(data)
    } catch {
      toast('세션 목록을 불러오지 못했습니다.', 'error')
    } finally {
      setLoadingSessions(false)
    }
  }

  const selectSession = async (session: any) => {
    setCurrentSession(session)
    setSidebarOpen(false)
    setLoadingMessages(true)
    try {
      const { data } = await client.get(`/counseling/sessions/${session.id}/messages`)
      setMessages(data)
    } catch {
      toast('메시지를 불러오지 못했습니다.', 'error')
    } finally {
      setLoadingMessages(false)
    }
  }

  const createSession = async (overrideMode?: 'chat' | 'qna') => {
    if (isCreating) return
    setIsCreating(true)
    try {
      const { data } = await client.post('/counseling/sessions', { category, mode: overrideMode ?? mode })
      await loadSessions()
      setCurrentSession(data)
      setMessages([])
      setShowNewSession(false)
      setSidebarOpen(false)
    } catch {
      toast('세션 생성에 실패했습니다.', 'error')
    } finally {
      setIsCreating(false)
    }
  }

  const handleSwitchToChat = async () => {
    const cat = currentSession?.category ?? category
    try {
      const { data } = await client.post('/counseling/sessions', { category: cat, mode: 'chat' })
      await loadSessions()
      setCurrentSession(data)
      setMessages([])
      toast('대화형 상담으로 이어집니다.', 'success')
    } catch {
      toast('세션 생성에 실패했습니다.', 'error')
    }
  }

  const sendMessage = async (text: string) => {
    if (!currentSession || streaming) return
    addMessage({ role: 'user', content: text })
    setStreaming(true)
    clearStreamingContent()

    if (currentSession.mode === 'chat') {
      try {
        const token = localStorage.getItem('access_token')
        const res = await fetch(`/api/counseling/sessions/${currentSession.id}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ content: text }),
        })
        if (!res.ok) throw new Error(`서버 오류 ${res.status}`)
        const reader = res.body!.getReader()
        const decoder = new TextDecoder()
        let full = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          const lines = decoder.decode(value).split('\n')
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const raw = line.slice(6)
              if (raw === '[DONE]') break
              try {
                const chunk = JSON.parse(raw) as string
                full += chunk
                appendStreamingContent(chunk)
              } catch {
                full += raw
                appendStreamingContent(raw)
              }
            }
          }
        }
        addMessage({ role: 'assistant', content: full })
        clearStreamingContent()
      } catch (e: any) {
        toast(e.message || 'AI 응답 중 오류가 발생했습니다.', 'error')
        clearStreamingContent()
      } finally {
        setStreaming(false)
      }
    } else {
      try {
        const { data } = await client.post(`/counseling/sessions/${currentSession.id}/chat`, { content: text })
        addMessage({ role: 'assistant', content: data.content })
      } catch {
        toast('AI 응답 중 오류가 발생했습니다.', 'error')
      } finally {
        setStreaming(false)
        clearStreamingContent()
      }
    }
  }

  return (
    <div className="flex h-screen bg-[var(--color-bg)] overflow-hidden">

      {/* 모바일 오버레이 */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* 사이드바 */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30
        w-64 flex flex-col border-r border-[var(--color-border-default)] bg-[var(--color-surface)]
        transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 border-b border-[var(--color-border-default)] flex items-center justify-between">
          <div>
            <h1 className="text-[var(--color-accent)] font-bold text-base">🏪 운명편의점</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs mt-0.5">
              {user?.nickname ? `${user.nickname}님, 어서오세요` : '어서오세요'}
            </p>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1">✕</button>
        </div>

        <div className="p-3">
          <button onClick={() => setShowNewSession(true)}
            className="w-full py-2 bg-[var(--color-accent)] hover:opacity-90 rounded-xl text-[var(--color-primary)] text-sm font-bold transition-opacity">
            + 새 상담 시작
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 pb-2 space-y-1">
          {loadingSessions ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : sessions.length === 0 ? (
            <p className="text-[var(--color-text-disabled)] text-xs text-center py-6">상담 기록이 없습니다</p>
          ) : sessions.map((s) => (
            <button key={s.id} onClick={() => selectSession(s)}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${
                currentSession?.id === s.id
                  ? 'bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30 text-[var(--color-accent)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-primary)]/20 hover:text-[var(--color-text-primary)]'
              }`}>
              <div className="font-medium truncate">{s.title || '새 상담'}</div>
              <div className="text-xs text-[var(--color-text-disabled)] mt-0.5">
                {CATEGORY_LABELS[s.category]}
                <span className="ml-1">· {s.mode === 'chat' ? '대화형' : 'Q&A형'}</span>
              </div>
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-[var(--color-border-default)] space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full text-left px-3 py-2 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-primary)]/20 transition-colors">
            🏪 대시보드
          </button>
          <button onClick={() => navigate('/history')} className="w-full text-left px-3 py-2 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-primary)]/20 transition-colors">
            📋 상담 기록
          </button>
          <button onClick={() => navigate('/profile')} className="w-full text-left px-3 py-2 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-primary)]/20 transition-colors">
            👤 내 정보
          </button>
          <button onClick={() => { logout(); navigate('/login') }} className="w-full text-left px-3 py-2 rounded-xl text-sm text-[var(--color-text-tertiary)] hover:text-[var(--color-error)] hover:bg-[var(--color-error)]/10 transition-colors">
            또 오세요 👋
          </button>
        </div>
      </aside>

      {/* 메인 */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* 새 세션 모달 */}
        {showNewSession && (
          <div className="fixed inset-0 z-10 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-6 w-full max-w-sm shadow-2xl">
              <h2 className="text-[var(--color-text-primary)] font-bold text-lg mb-4">새 상담 시작</h2>
              <p className="text-[var(--color-text-tertiary)] text-sm mb-3">상담 카테고리</p>
              <CategorySelector selected={category} onChange={setCategory} />
              <div className="flex gap-3 mt-4">
                {[['chat', '💬 대화형'], ['qna', '❓ Q&A형']] .map(([v, label]) => (
                  <button key={v} onClick={() => setMode(v as 'chat' | 'qna')}
                    className={`flex-1 py-2 rounded-xl text-sm border transition-colors ${mode === v ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] font-bold' : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {mode === 'qna' && (
                <p className="text-[var(--color-text-disabled)] text-xs mt-2 text-center">단발성 질문 · 빠른 답변 · 대화 기록 없음</p>
              )}
              <div className="flex gap-3 mt-5">
                <button onClick={() => setShowNewSession(false)} className="flex-1 py-2.5 rounded-xl border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] text-sm hover:text-[var(--color-text-primary)] transition-colors">취소</button>
                <button onClick={() => createSession()} disabled={isCreating} className="flex-1 py-2.5 rounded-xl bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-primary)] text-sm font-bold transition-opacity">
                  {isCreating ? '생성 중...' : '시작'}
                </button>
              </div>
            </div>
          </div>
        )}

        {currentSession ? (
          <>
            {/* 헤더 */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--color-border-default)] bg-[var(--color-primary)]">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[var(--color-secondary)] hover:text-[var(--color-text-primary)] p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="min-w-0">
                <h2 className="text-[var(--color-text-primary)] font-semibold truncate">{currentSession.title || '새 상담'}</h2>
                <p className="text-[var(--color-secondary)] text-xs">{CATEGORY_LABELS[currentSession.category]} · {currentSession.mode === 'chat' ? '대화형' : 'Q&A형'}</p>
              </div>
            </div>

            {/* 채팅 + 사주 카드 */}
            <div className="flex flex-1 overflow-hidden">
              {currentSession.mode === 'qna' ? (
                <QnaPanel session={currentSession} onSwitchToChat={handleSwitchToChat} />
              ) : (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-1 overflow-y-auto px-4 py-4">
                    {loadingMessages ? (
                      <div className="flex justify-center mt-20"><Spinner size="lg" /></div>
                    ) : messages.length === 0 ? (
                      <div className="text-center text-[var(--color-text-tertiary)] text-sm mt-20">
                        <p className="text-4xl mb-4">🏪</p>
                        <p>무엇이 궁금하신가요?<br />편하게 고민을 말씀해 주세요.</p>
                      </div>
                    ) : (
                      messages.map((m, i) => <ChatMessage key={i} role={m.role} content={m.content} />)
                    )}
                    {streaming && streamingContent && (
                      <ChatMessage role="assistant" content={streamingContent} streaming />
                    )}
                    {streaming && !streamingContent && (
                      <div className="flex justify-start mb-4">
                        <div className="w-8 h-8 rounded-full bg-[var(--color-accent)]/20 border border-[var(--color-accent)]/30 flex items-center justify-center text-sm mr-2">🏪</div>
                        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl rounded-bl-sm px-4 py-3">
                          <div className="flex gap-1">
                            {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 bg-[var(--color-accent)] rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={bottomRef} />
                  </div>
                  <div className="px-4 py-3 border-t border-[var(--color-border-default)]">
                    <ChatInput onSend={sendMessage} disabled={streaming} />
                  </div>
                </div>
              )}

              <div className="hidden xl:block w-64 p-4 border-l border-[var(--color-border-default)] overflow-y-auto flex-shrink-0">
                <SajuCard />
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--color-border-default)] bg-[var(--color-primary)] lg:hidden">
              <button onClick={() => setSidebarOpen(true)} className="text-[var(--color-secondary)] hover:text-[var(--color-text-primary)] p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <span className="text-[var(--color-accent)] font-bold">🏪 운명편의점</span>
            </div>
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div>
                <p className="text-6xl mb-6">🏪</p>
                <h2 className="text-[var(--color-text-primary)] text-xl font-semibold mb-2">운명편의점에 오신 것을 환영합니다</h2>
                <p className="text-[var(--color-text-tertiary)] text-sm mb-6">새 상담을 시작하거나 이전 상담을 선택하세요</p>
                <button onClick={() => setShowNewSession(true)}
                  className="px-6 py-3 bg-[var(--color-accent)] hover:opacity-90 rounded-xl text-[var(--color-primary)] font-bold transition-opacity">
                  새 상담 시작하기
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
